import { useEffect, useRef, useState } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { useInterval } from '@/hooks/use-interval.ts';
import Logo from '@tenant/logo';
import tenantConfig from '@tenant/config';
import { usePostLogErrorToBackend } from '@/features/feedback/hooks/mutations/use-post-log-error-to-backend.ts';

const ErrorPage = () => {
  const hasFired = useRef(false);
  const [countdown, setCountdown] = useState(10);

  const routeError: unknown = useRouteError();
  const navigate = useNavigate();

  const logErrorMutation = usePostLogErrorToBackend();

  let errorTitle = 'Oops! Something unexpected happened';
  let errorMessage =
    "We've detected an issue and our team has been automatically notified. We'll resolve this as quickly as possible while you continue using our application.";

  if (routeError && isRouteErrorResponse(routeError)) {
    if (routeError.status === 404) {
      errorTitle = 'Page not found';
      errorMessage = "The page you're looking for doesn't exist or has been moved.";
    } else {
      errorTitle = 'Oops! Something unexpected happened';
      errorMessage = `We've encountered an issue (${routeError.status}) and our team has been automatically notified.`;
    }
  }

  useInterval(() => {
    setCountdown((prev) => {
      if (prev <= 1) {
        navigate('/', { replace: true });
        return 0;
      }
      return prev - 1;
    });
  }, 1000);

  useEffect(() => {
    if (!hasFired.current) {
      hasFired.current = true;

      logErrorMutation.mutate(
        routeError instanceof Error ? routeError : new Error(`${errorTitle}. ${errorMessage}`)
      );
    }
  }, []);

  return (
    <div className="flex h-svh flex-col justify-between bg-white">
      <main className="flex flex-1 flex-col items-center justify-center px-4">
        <div className="flex w-full max-w-2xl flex-col items-center gap-12">
          <Logo
            width="100%"
            height="100%"
            withText={false}
            className="size-[100px] sm:size-[150px]"
          />
          <div className="flex flex-col items-center gap-6 text-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {errorTitle}
            </h1>
            <p className="text-base leading-7 text-gray-600 sm:text-lg sm:leading-8">
              {errorMessage}
            </p>
            <p className="text-sm text-gray-500">
              You'll be redirected to the home page in {countdown} seconds.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/', { replace: true })}
                className="rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Go to Home
              </button>
              <a
                href={`mailto:${tenantConfig.support.email}`}
                className="rounded-md bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-200"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ErrorPage;
